import { ref, computed, unref, watch } from 'vue';
import { useToast } from '@/composables/common/useToast';
import { useSeatReleaseRules } from '@/composables/front/perfil/useSeatReleaseRules';
import { getDay, getMonth, getDayName } from '@/utils/dateUtils';
import { t } from '@/utils/i18n-helper';
import abonosService from '@/services/perfil/abonos';
import store from '@/store';

export function useLiberarAsiento(abonoIdSource, onBack) {
    const toast = useToast();
    const teatro = computed(() => store.getters.getTeatro);
    const { usesPoints, refundTextKey } = useSeatReleaseRules(teatro);

    const viewMode = ref('eventos');
    const selectedEvento = ref(null);

    const loadingEventos = ref(false);
    const errorEventos = ref(null);
    const eventos = ref([]);

    const loadingSesiones = ref(false);
    const errorSesiones = ref(null);
    const sesiones = ref([]);

    // Track request IDs to discard stale responses (race condition guard)
    let lastRequestedEventoId = null;
    let lastRequestedAbonoId = null;

    const selectedSesion = ref(null);
    const showLiberarModal = ref(false);

    const hasEventos = computed(() => !loadingEventos.value && !errorEventos.value && eventos.value.length > 0);
    const hasSesiones = computed(() => !loadingSesiones.value && !errorSesiones.value && sesiones.value.length > 0);
    const isSesionesEmpty = computed(() => !loadingSesiones.value && !errorSesiones.value && sesiones.value.length === 0);

    const getAbonoId = () => unref(abonoIdSource);

    const resetState = () => {
        viewMode.value = 'eventos';
        selectedEvento.value = null;
        eventos.value = [];
        sesiones.value = [];
        errorEventos.value = null;
        errorSesiones.value = null;
        selectedSesion.value = null;
        showLiberarModal.value = false;
        lastRequestedEventoId = null;
    };

    const refundAmount = evento => evento.importe_monedero_abonado;
    const refundUsesPoints = usesPoints;

    const fetchEventos = async() => {
        const abonoId = getAbonoId();
        if (!abonoId) return;

        // Guardar el ID de esta petición para descartar respuestas obsoletas
        lastRequestedAbonoId = abonoId;
        loadingEventos.value = true;
        errorEventos.value = null;

        try {
            const result = await abonosService.getEventosLiberarAsiento(abonoId);
            // Verificar que la respuesta es para el abono activo ANTES de asignar
            if (lastRequestedAbonoId !== abonoId) {
                return;
            }
            eventos.value = result;
        } catch (err) {
            if (lastRequestedAbonoId !== abonoId) {
                return;
            }
            errorEventos.value = err?.message ?? t('perfil.liberar_error_eventos');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useLiberarAsiento] Error al obtener eventos:', err);
            }
        } finally {
            if (lastRequestedAbonoId === abonoId) {
                loadingEventos.value = false;
            }
        }
    };

    const fetchSesiones = async eventoId => {
        const abonoId = getAbonoId();
        if (!abonoId) return;

        // Guardar el ID de esta petición para descartar respuestas obsoletas
        lastRequestedEventoId = eventoId;
        loadingSesiones.value = true;
        errorSesiones.value = null;

        try {
            const result = await abonosService.getSesionesLiberarAsiento(abonoId, eventoId);
            // Verificar que la respuesta es para el evento activo ANTES de asignar
            if (lastRequestedEventoId !== eventoId) {
                return;
            }
            sesiones.value = result;
        } catch (err) {
            if (lastRequestedEventoId !== eventoId) {
                return;
            }
            errorSesiones.value = err?.message ?? t('perfil.liberar_error_sesiones');
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useLiberarAsiento] Error al obtener sesiones:', err);
            }
        } finally {
            if (lastRequestedEventoId === eventoId) {
                loadingSesiones.value = false;
            }
        }
    };

    const selectEvento = evento => {
        selectedEvento.value = evento;
        viewMode.value = 'sesiones';
        fetchSesiones(evento.id);
    };

    const goBack = () => {
        if (viewMode.value === 'sesiones') {
            viewMode.value = 'eventos';
            selectedEvento.value = null;
            sesiones.value = [];
        } else {
            onBack?.();
        }
    };

    const openLiberarModal = sesion => {
        selectedSesion.value = sesion;
        showLiberarModal.value = true;
    };

    const closeLiberarModal = () => {
        showLiberarModal.value = false;
        selectedSesion.value = null;
    };

    const handleLiberarAsiento = async() => {
        const abonoId = getAbonoId();
        if (!abonoId || !selectedSesion.value || !selectedEvento.value) return;

        try {
            const result = await abonosService.liberarAsiento(
                abonoId,
                selectedEvento.value.id,
                selectedSesion.value.id
            );
            toast.success(result.message?.message || t('perfil.asiento_liberado'));
            closeLiberarModal();
            fetchSesiones(selectedEvento.value.id);
            fetchEventos();
        } catch (err) {
            const errorMessage = err.response?.data?.message?.error || err.message || t('perfil.liberar_error_asiento');
            toast.error(errorMessage);
        }
    };

    watch(
        () => getAbonoId(),
        newAbonoId => {
            resetState();

            if (newAbonoId) {
                fetchEventos();
            }
        },
        { immediate: true }
    );

    return {
        viewMode,
        selectedEvento,
        eventos,
        sesiones,
        loadingEventos,
        loadingSesiones,
        errorEventos,
        errorSesiones,
        showLiberarModal,
        selectedSesion,
        hasEventos,
        hasSesiones,
        isSesionesEmpty,
        refundAmount,
        refundUsesPoints,
        selectEvento,
        goBack,
        openLiberarModal,
        closeLiberarModal,
        handleLiberarAsiento
    };
}